import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { TextField, Autocomplete } from '@avangard/ui/core';
import {
    FormControlLabel,
    withStyles,
    MenuItem,
    FormGroup,
    Radio,
    Tooltip,
} from '@material-ui/core';
import { InfoOutlined } from '@material-ui/icons';

import { useRegions, useLazyLocations } from '@modules/shared/hooks';
import { FormStyled } from '@modules/layout/styled';
import { useCurrentLap } from '@modules/lap/hooks';
import { schoolTypeTranslates, studentsAddModeOptions } from '@modules/schools/constants';
import { SchoolType, StudentsAddMode } from '@modules/types/graphql';

import type { RegionEntity, LocationEntity } from '@modules/shared/entities';
import type { SchoolEntity } from '@modules/schools/entities';
import type { UpdateSchoolFormValues } from '@modules/schools/requests';

type SchoolSummaryEditFormGroupProps = {
    school: SchoolEntity;
};

const StyledFormColumnTitle = styled(FormStyled.FormColumnTitle)`
    display: flex;
    align-items: center;

    & > *:last-child {
        margin-top: 4px;
        margin-inline-start: 8px;
        display: flex;
        justify-content: center;
        align-items: center;
    }
`;

const StyledFormGroup = withStyles({
    root: {
        margin: '-8px 0 24px -4px',

        '& > *': {
            width: '100%',
        },
    },
})(FormGroup);

const StyledTextField = withStyles({
    root: {
        marginTop: '-16px',
    },
})(TextField);

const schoolTypes = Object.values(SchoolType);

const infoOutlinedIcon = <InfoOutlined style={{ fontSize: 20 }} />;

const SchoolSummaryEditFormGroup = (props: SchoolSummaryEditFormGroupProps): React.ReactElement => {
    const { school } = props;

    const { t } = useTranslation(['common', 'errors', 'schools']);

    const { values, errors, handleChange, handleBlur, setFieldValue } =
        useFormikContext<UpdateSchoolFormValues>();

    const { currentLap, loading: currentLapLoading } = useCurrentLap();

    const { regions, loading: regionsLoading } = useRegions();
    const [getLocations, { locations, count: locationsCount, loading: locationsLoading }] =
        useLazyLocations();

    React.useEffect(() => {
        if (!values.region?.id) {
            return;
        }

        getLocations({ variables: { regionId: values.region.id } });
    }, [values.region?.id]);

    const handleChangeSelect = (event: React.ChangeEvent<{ name: string; value: string }>) =>
        setFieldValue(event.target.name, event.target.value);

    const handleChangeAutocomplete = (id: string) => (_: React.ChangeEvent<{}>, value: any) => {
        setFieldValue(id, value);

        if (id === 'region') {
            setFieldValue('location', null);
        }
    };

    const handleChangeStudentsAddMode = (
        event: React.ChangeEvent<{ name: string; value: string }>,
    ) => {
        setFieldValue('studentsAddMode', event.target.value);
        setFieldValue('maxStudentsCount', '');
    };

    const getOptionLabel =
        (field: string) =>
        (option: any | null): string =>
            option?.[field] || '-';

    const hasCurrentLap = !currentLapLoading && currentLap.exists();
    const hasLapAccess = school.lapAccessList.length > 0;
    const canChangeStudentsCount = hasCurrentLap && hasLapAccess;

    const hasStudentsLimit = values.studentsAddMode === StudentsAddMode.limited;

    return (
        <>
            <FormStyled.FormColumn>
                <FormStyled.FormColumnTitle>
                    {t('common:forms.titles.summary')}
                </FormStyled.FormColumnTitle>

                <TextField
                    fullWidth
                    disabled
                    id='fullName'
                    label={t('common:forms.labels.id')}
                    value={values.id}
                />

                <Autocomplete<RegionEntity | null, false, false, false>
                    required
                    fullWidth
                    id='region'
                    label={t('common:forms.labels.region') ?? ''}
                    placeholder={t('common:forms.placeholder.choose_region') ?? ''}
                    options={regions}
                    value={values.region}
                    error={!!errors.region}
                    helperText={errors.region}
                    disabled={regionsLoading}
                    onChange={handleChangeAutocomplete('region')}
                    getOptionLabel={getOptionLabel('name')}
                />

                <Autocomplete<LocationEntity | null, false, false, false>
                    required
                    fullWidth
                    id='location'
                    label={t('common:forms.labels.location') ?? ''}
                    placeholder={t('common:forms.placeholder.choose_location') ?? ''}
                    options={locations}
                    value={values.location}
                    error={!!errors.location}
                    helperText={errors.location}
                    disabled={!values.region || locationsCount === 0 || locationsLoading}
                    onChange={handleChangeAutocomplete('location')}
                    getOptionLabel={getOptionLabel('name')}
                />

                <TextField
                    select
                    required
                    fullWidth
                    id='type'
                    name='type'
                    label={t('common:forms.labels.school_type')}
                    value={values.type}
                    error={!!errors.type}
                    helperText={errors.type}
                    onChange={handleChangeSelect}
                    onBlur={handleBlur}
                >
                    <MenuItem value=''>
                        {t('common:forms.placeholder.choose_school_type_long')}
                    </MenuItem>

                    {schoolTypes.map(schoolType => (
                        <MenuItem key={schoolType} value={schoolType}>
                            {t(schoolTypeTranslates[schoolType])}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    required
                    fullWidth
                    id='shortName'
                    label={t('common:forms.labels.school_short_name')}
                    placeholder={t('common:forms.placeholder.typing_school_short_name') ?? ''}
                    value={values.shortName}
                    error={!!errors.shortName}
                    helperText={errors.shortName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />

                <TextField
                    multiline
                    fullWidth
                    rows={4}
                    id='fullName'
                    label={t('common:forms.labels.school_long_name')}
                    placeholder={t('common:forms.placeholder.typing_school_long_name') ?? ''}
                    value={values.fullName}
                    error={!!errors.fullName}
                    helperText={errors.fullName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />

                <TextField
                    fullWidth
                    id='number'
                    label={t('common:forms.labels.school_number')}
                    placeholder={t('common:forms.placeholder.typing_school_number') ?? ''}
                    value={values.number}
                    error={!!errors.number}
                    helperText={errors.number}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />

                <TextField
                    fullWidth
                    id='profile'
                    label={t('schools:form.labels.profile')}
                    placeholder={t('schools:form.placeholder.typing_profile') ?? ''}
                    value={values.profile}
                    error={!!errors.profile}
                    helperText={errors.profile}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />

                <TextField
                    fullWidth
                    id='comment'
                    label={t('schools:form.labels.comment')}
                    placeholder={t('schools:form.placeholder.typing_comment') ?? ''}
                    value={values.comment}
                    error={!!errors.comment}
                    helperText={errors.comment}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
            </FormStyled.FormColumn>

            <FormStyled.FormColumn>
                <FormStyled.FormColumnTitle>
                    {t('common:forms.titles.contacts')}
                </FormStyled.FormColumnTitle>

                <TextField
                    fullWidth
                    id='responsiblePerson'
                    label={t('common:forms.labels.responsible_full_name')}
                    placeholder={t('common:forms.placeholder.typing_full_name') ?? ''}
                    value={values.responsiblePerson}
                    error={!!errors.responsiblePerson}
                    helperText={errors.responsiblePerson}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />

                <TextField
                    fullWidth
                    id='phone'
                    label={t('common:forms.labels.phone')}
                    placeholder={t('common:forms.placeholder.phone') ?? ''}
                    value={values.phone}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />

                <TextField
                    required
                    fullWidth
                    id='email'
                    label={t('common:forms.labels.email')}
                    placeholder={t('common:forms.placeholder.typing_email') ?? ''}
                    value={values.email}
                    error={!!errors.email}
                    helperText={errors.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />

                {canChangeStudentsCount ? (
                    <>
                        <StyledFormColumnTitle>
                            {t('schools:form.titles.add_students')}
                            <Tooltip
                                arrow
                                title={t('schools:form.labels.restrict') ?? ''}
                                placement='bottom'
                            >
                                <span>{infoOutlinedIcon}</span>
                            </Tooltip>
                        </StyledFormColumnTitle>

                        <StyledFormGroup row>
                            {studentsAddModeOptions.map(studentsAddMode => {
                                const isChecked = values.studentsAddMode === studentsAddMode.key;

                                return (
                                    <FormControlLabel
                                        key={studentsAddMode.key}
                                        label={t(studentsAddMode.value)}
                                        control={
                                            <Radio
                                                name='studentsAddMode'
                                                value={studentsAddMode.key}
                                                checked={isChecked}
                                                onChange={handleChangeStudentsAddMode}
                                            />
                                        }
                                    />
                                );
                            })}
                        </StyledFormGroup>

                        {hasStudentsLimit ? (
                            <StyledTextField
                                fullWidth
                                required
                                type='number'
                                id='maxStudentsCount'
                                label={t('schools:form.labels.max_students_count')}
                                placeholder={
                                    t('schools:form.placeholder.typing_max_students_count') ?? ''
                                }
                                value={values.maxStudentsCount}
                                error={!!errors.maxStudentsCount}
                                inputProps={{ min: 1 }}
                                helperText={errors.maxStudentsCount}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        ) : null}
                    </>
                ) : null}
            </FormStyled.FormColumn>
        </>
    );
};

export { SchoolSummaryEditFormGroup };
